import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { Run, RunFilters, RunStatus, StageProgress } from '@/types';
import { api } from '@/services/api';
import { useConnectorStore } from './useConnectorStore';
import { logger, logUserAction, logApiError } from '@/logging';

interface DateFilter {
  startDate?: string;
  endDate?: string;
}

interface RunPagination {
  page: number;
  pageSize: number;
}

interface RunStats {
  total: number;
  running: number;
  pending: number;
  completed: number;
  failed: number;
  cancelled: number;
}

interface RunState {
  // State
  runs: Run[];
  selectedRun: Run | null;
  loading: boolean;
  error: Error | null;
  filters: RunFilters;
  dateFilter: DateFilter;
  pagination: RunPagination;
  stateVersion: number;
  clearedRunIds: Set<string>;

  // Computed
  activeRuns: Run[];
  filteredRuns: Run[];
  paginatedRuns: Run[];
  totalPages: number;
  runStats: RunStats;

  // Actions
  fetchRuns: () => Promise<void>;
  setFilters: (filters: RunFilters) => void;
  clearRuns: (statuses: string[]) => void;
  clearRun: (runId: string) => void;
  resetClearedRuns: () => void;
  startRun: (connectorId: string, fullResync?: boolean) => Promise<Run>;
  cancelRun: (runId: string) => Promise<void>;
  getRunDetails: (runId: string) => Promise<void>;
  setRuns: (runs: Run[]) => void;
  updateRun: (run: Run) => void;
  clearActiveRuns: () => void;
  updateRunProgress: (
    runId: string,
    progress: Partial<Run['progress']>
  ) => void;
  selectRun: (runId: string | null) => void;
  setDateFilter: (filter: DateFilter) => void;
  setPagination: (pagination: RunPagination) => void;
  goToPage: (page: number) => void;
}

const defaultPagination: RunPagination = { page: 1, pageSize: 20 };

// LocalStorage key for cleared runs
const CLEARED_RUNS_STORAGE_KEY = 'cortexflow-cleared-runs';

// Helper to load cleared runs from localStorage
const loadClearedRunIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(CLEARED_RUNS_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

// Helper to save cleared runs to localStorage
const saveClearedRunIds = (clearedIds: Set<string>) => {
  try {
    localStorage.setItem(
      CLEARED_RUNS_STORAGE_KEY,
      JSON.stringify(Array.from(clearedIds))
    );
  } catch (e) {
    console.warn('Failed to save cleared runs to localStorage:', e);
  }
};

export const useRunStore = create<RunState>()(
  immer((set, get) => ({
    // Initial state
    runs: [],
    selectedRun: null,
    loading: false,
    error: null,
    filters: {},
    dateFilter: {},
    pagination: defaultPagination,
    stateVersion: 0,
    clearedRunIds: loadClearedRunIds(),

    // Computed getters
    get activeRuns() {
      return get().runs.filter(run => run.status === 'running');
    },

    get filteredRuns() {
      const { runs, filters, dateFilter } = get();
      let filtered = [...runs];

      // Apply status filter
      if (filters.status) {
        filtered = filtered.filter(run => run.status === filters.status);
      }

      // Apply connector filter
      if (filters.connectorId) {
        filtered = filtered.filter(
          run => run.connectorId === filters.connectorId
        );
      }

      // Apply date filter
      if (dateFilter.startDate) {
        const startDate = new Date(dateFilter.startDate).getTime();
        filtered = filtered.filter(
          run => new Date(run.startTime).getTime() >= startDate
        );
      }

      if (dateFilter.endDate) {
        const endDate = new Date(dateFilter.endDate + 'T23:59:59').getTime();
        filtered = filtered.filter(
          run => new Date(run.startTime).getTime() <= endDate
        );
      }

      // Sort by start time descending (newest first)
      filtered.sort(
        (a, b) =>
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      );

      return filtered;
    },

    get paginatedRuns() {
      const { filteredRuns, pagination } = get();
      const start = (pagination.page - 1) * pagination.pageSize;
      const end = start + pagination.pageSize;
      return filteredRuns.slice(start, end);
    },

    get totalPages() {
      const { filteredRuns, pagination } = get();
      return Math.ceil(filteredRuns.length / pagination.pageSize);
    },

    get runStats() {
      const runs = get().runs;

      const stats = {
        total: runs.length,
        running: runs.filter(r => r.status === 'running').length,
        pending: runs.filter(r => r.status === 'pending').length,
        completed: runs.filter(r => r.status === 'completed').length,
        failed: runs.filter(r => r.status === 'failed').length,
        cancelled: runs.filter(r => r.status === 'cancelled').length,
      };

      return stats;
    },

    // Actions
    fetchRuns: async () => {
      set(state => {
        state.loading = true;
        state.error = null;
      });

      try {
        // Ensure connectors are fetched first if not already available
        const connectorStore = useConnectorStore.getState();
        if (connectorStore.connectors.length === 0) {
          await connectorStore.fetchConnectors();
        }

        const runs = await api.runs.list();

        // Filter out malformed runs and deduplicate by ID
        const seenIds = new Set<string>();
        const validRuns = runs.filter(run => {
          // Check if run has valid structure
          if (!run.id || typeof run.id !== 'string') {
            console.warn('Filtering out run with invalid ID:', run);
            return false;
          }

          // Log warning if connectorId is missing
          if (!run.connectorId) {
            console.warn('Run missing connectorId:', run.id);
          }

          // Check for duplicate IDs
          if (seenIds.has(run.id)) {
            console.warn('Filtering out duplicate run ID:', run.id);
            return false;
          }
          seenIds.add(run.id);

          // Accept runs with namespace prefix (e.g., "mk:run-...") or direct "run-" prefix
          const idParts = run.id.split(':');
          const runIdPart =
            idParts.length > 1 ? idParts[idParts.length - 1] : run.id;
          if (!runIdPart.startsWith('run-')) {
            console.warn('Filtering out run without proper prefix:', run.id);
            return false;
          }

          return true;
        });

        set(state => {
          // Get connectors to map connector names
          const connectors = useConnectorStore.getState().connectors;
          const connectorMap = new Map(connectors.map(c => [c.id, c.name]));

          // Add connector names to runs - ensure proper mapping
          const runsWithNames = validRuns.map(run => {
            // Try to get connector ID from various possible fields
            const connectorId = run.connectorId || run.connectorScope;

            // If run has embedded connector info, use that
            let connectorName = run.connectorName;

            // If run has connectors object, extract the name from there
            if (!connectorName && run.connectors && connectorId) {
              const connectorInfo = run.connectors[connectorId];
              if (connectorInfo && connectorInfo.name) {
                connectorName = connectorInfo.name;
              }
            }

            // Fallback to connector map from store
            if (!connectorName && connectorId) {
              connectorName = connectorMap.get(connectorId) || '';
            }

            // Final fallback
            if (!connectorName) {
              connectorName = connectorId
                ? `Connector ${connectorId.substring(0, 8)}...`
                : 'Unknown Connector';
            }

            return {
              ...run,
              connectorId: connectorId || run.connectorId || '',
              connectorName,
            };
          });

          // Log fetched runs for debugging
          logger.debug('Fetched runs', {
            totalFetched: runs.length,
            validRuns: validRuns.length,
            runIds: validRuns
              .slice(0, 5)
              .map(r => ({ id: r.id, status: r.status })),
          });

          // Get locally cancelled run IDs
          const locallyCancelledIds = state.runs
            .filter(r => r.status === 'cancelled' && r.endTime)
            .map(r => r.id);

          // Filter out cleared runs
          const filteredRuns = runsWithNames.filter(
            run => !state.clearedRunIds.has(run.id)
          );

          // Preserve locally cancelled runs that haven't been cleared
          const finalRuns = filteredRuns.map(run => {
            const localRun = state.runs.find(r => r.id === run.id);
            if (localRun && locallyCancelledIds.includes(run.id)) {
              return {
                ...run,
                status: 'cancelled' as RunStatus,
                endTime: localRun.endTime,
              };
            }
            return run;
          });

          state.runs = finalRuns;
          state.loading = false;
          state.stateVersion = (state.stateVersion || 0) + 1;
        });
      } catch (error) {
        set(state => {
          state.error = error as Error;
          state.loading = false;
        });
        throw error;
      }
    },

    startRun: async (connectorId, fullResync = false) => {
      logUserAction('Start Run', {
        connectorId,
        fullResync,
        timestamp: new Date().toISOString(),
      });

      try {
        const run = await api.runs.start(connectorId, fullResync);

        logger.info('Run started successfully', {
          runId: run.id,
          connectorId,
          fullResync,
          status: run.status,
        });

        // Get connector name
        const connectors = useConnectorStore.getState().connectors;
        const runConnectorId = run.connectorId || run.connectorScope || connectorId;

        // Try multiple sources for connector name
        let connectorName = run.connectorName;

        // Check embedded connector info
        if (!connectorName && run.connectors && runConnectorId) {
          const connectorInfo = run.connectors[runConnectorId];
          if (connectorInfo && connectorInfo.name) {
            connectorName = connectorInfo.name;
          }
        }

        // Check connector store
        if (!connectorName) {
          const connector = connectors.find(c => c.id === runConnectorId);
          connectorName = connector?.name || '';
        }

        // Fallback
        if (!connectorName) {
          connectorName = runConnectorId || 'Unknown Connector';
        }

        const runWithName = {
          ...run,
          connectorId: runConnectorId || '',
          connectorName,
        };

        set(state => {
          // Don't add if this run was previously cleared
          if (!state.clearedRunIds.has(run.id)) {
            state.runs.unshift(runWithName); // Add to beginning
          }
        });

        return runWithName;
      } catch (error) {
        logApiError(error, {
          action: 'startRun',
          connectorId,
          fullResync,
        });
        throw error;
      }
    },

    cancelRun: async runId => {
      // Optimistic update
      set(state => {
        const run = state.runs.find(r => r.id === runId);
        if (run) {
          run.status = 'cancelled';
          run.endTime = new Date().toISOString();
        }
      });

      try {
        await api.runs.cancel(runId);
        // Keep the optimistic update on success
      } catch (error) {
        // On error, keep the local cancellation but log the error
        console.error(`Failed to cancel run ${runId} on server:`, error);
        // Don't revert the optimistic update to prevent the run from reappearing
        // The user has expressed intent to cancel, so honor that locally
        // The server will eventually catch up or handle it via other means
      }
    },

    getRunDetails: async runId => {
      set(state => {
        state.loading = true;
      });

      logger.debug('Fetching run details', { runId });

      try {
        const run = await api.runs.get(runId);

        logger.debug('Run details fetched', {
          runId,
          status: run.status,
          progress: run.progress,
        });

        // Get connector name
        const connectors = useConnectorStore.getState().connectors;
        const connectorId = run.connectorId || run.connectorScope;

        // Try multiple sources for connector name
        let connectorName = run.connectorName;

        // Check embedded connector info
        if (!connectorName && run.connectors && connectorId) {
          const connectorInfo = run.connectors[connectorId];
          if (connectorInfo && connectorInfo.name) {
            connectorName = connectorInfo.name;
          }
        }

        // Check connector store
        if (!connectorName && connectorId) {
          const connector = connectors.find(c => c.id === connectorId);
          connectorName = connector?.name || '';
        }

        // Fallback
        if (!connectorName) {
          connectorName = connectorId || 'Unknown Connector';
        }

        const runWithName = {
          ...run,
          connectorId: connectorId || run.connectorId || '',
          connectorName,
        };

        // Check if this is a "no changes detected" run that should be marked as completed
        if (
          'discoveryMetadata' in run &&
          run.discoveryMetadata &&
          run.discoveryMetadata.reason === 'no_changes' &&
          run.progress.totalDocs === 0 &&
          run.status === 'running'
        ) {
          logger.info(
            'Marking "no changes detected" run as completed in getRunDetails',
            {
              runId: run.id,
              discoveryMetadata: run.discoveryMetadata,
            }
          );
          runWithName.status = 'completed';
          // Don't set endTime here - trust the API response from backend
          // The backend (RunStatus.update_aggregates) already sets endTime correctly
        }

        // If run is active, also fetch progress details
        if (run.status === 'running' || run.status === 'processing') {
          try {
            const progressData = await api.runs.getProgress(runId);

            logger.debug('Run progress details fetched', {
              runId,
              isComplete: progressData.isComplete,
              hasFailures: progressData.hasFailures,
            });

            // Update run with stage counts and chunk progress
            // Convert Record<string, Record<string, number>> to Record<string, StageProgress>
            if (progressData.stageCounts) {
              const convertedStageCounts: Record<string, StageProgress> = {};
              Object.entries(progressData.stageCounts).forEach(([stage, counts]) => {
                convertedStageCounts[stage] = {
                  queued: counts.queued || 0,
                  processing: counts.processing || 0,
                  complete: counts.complete || 0,
                  failed: counts.failed || 0,
                  totalProcessed: counts.totalProcessed || 0,
                };
              });
              runWithName.stageCounts = convertedStageCounts;
            }
            runWithName.chunkProgress = progressData.chunkProgress;

            if (progressData.stageCounts) {
              const indexCounts = progressData.stageCounts.index || {};
              const completedDocs = indexCounts.complete || 0;
              const failedDocs = indexCounts.failed || 0;

              // Don't override totalDocs from the run's progress with stage counts
              // Keep the original totalDocs from discovery
              const totalDocs = runWithName.progress.totalDocs || 0;

              // For chunked pipeline, use parent document completion percentage
              // Otherwise use the index stage counts
              const useChunks = (runWithName.progress.totalChunks ?? 0) > 0;
              let percentComplete = 0;

              if (useChunks && totalDocs > 0) {
                // Use parent document progress for chunked pipeline
                const parentComplete = runWithName.progress.parentComplete ?? 0;
                percentComplete = ((parentComplete + failedDocs) / totalDocs) * 100;
              } else if (totalDocs > 0) {
                // Use index completion for summary pipeline
                percentComplete = (completedDocs / totalDocs) * 100;
              }

              runWithName.progress = {
                ...runWithName.progress,
                processedDocs: completedDocs,
                failedDocs,
                percentComplete,
              };
            }

            // Update status if complete
            if (progressData.isComplete && progressData.status !== run.status) {
              runWithName.status = progressData.status as RunStatus;
              // Don't set endTime here - trust the API response from backend
              // The backend (RunStatus.update_aggregates) already sets endTime correctly
            }
          } catch (progressError) {
            // Log but continue - progress is optional enhancement
            logger.warn('Failed to fetch run progress details', {
              runId,
              error:
                progressError instanceof Error
                  ? progressError.message
                  : 'Unknown error',
            });
          }
        }

        set(state => {
          state.selectedRun = runWithName;
          state.loading = false;

          // Update in runs list if exists, preserving historical data
          const index = state.runs.findIndex(r => r.id === runId);
          if (index !== -1) {
            const existingRun = state.runs[index];

            // Preserve historical stage counts data
            if (existingRun.stageCounts && runWithName.stageCounts) {
              const mergedStageCounts: typeof runWithName.stageCounts = {};

              // First, copy all existing stage data (preserves history)
              Object.entries(existingRun.stageCounts).forEach(([stage, counts]) => {
                const total = Object.values(counts).reduce((sum: number, count) => sum + (typeof count === 'number' ? count : 0), 0);
                const completed = counts.complete || 0;
                const failed = counts.failed || 0;

                // Preserve stage if it had any data
                if ((typeof total === 'number' && total > 0) || completed > 0 || failed > 0) {
                  mergedStageCounts[stage] = { ...counts };
                }
              });

              // Then, update with new data
              Object.entries(runWithName.stageCounts).forEach(([stage, counts]) => {
                mergedStageCounts[stage] = counts;
              });

              runWithName.stageCounts = mergedStageCounts;
            }

            // Similarly preserve chunk progress data
            if (existingRun.chunkProgress && runWithName.chunkProgress) {
              const mergedChunksByStage = {
                ...existingRun.chunkProgress.chunksByStage,
                ...runWithName.chunkProgress.chunksByStage
              };

              const mergedChunkPercentages = {
                ...existingRun.chunkProgress.chunkPercentages,
                ...runWithName.chunkProgress.chunkPercentages
              };

              runWithName.chunkProgress = {
                ...runWithName.chunkProgress,
                chunksByStage: mergedChunksByStage,
                chunkPercentages: mergedChunkPercentages
              };
            }

            state.runs[index] = runWithName;
          }
        });
      } catch (error) {
        logApiError(error, {
          action: 'getRunDetails',
          runId,
        });
        set(state => {
          state.error = error as Error;
          state.loading = false;
        });
        throw error;
      }
    },


    setRuns: runs => {
      set(state => {
        state.runs = runs;
      });
    },

    updateRun: run => {
      set(state => {
        const idx = state.runs.findIndex(r => r.id === run.id);
        if (idx >= 0) {
          // Preserve historical stage counts data
          const existingRun = state.runs[idx];

          // Preserve connector name if missing in update
          if (!run.connectorName) {
            // Try to get connector ID from various possible fields
            const connectorId = run.connectorId || run.connectorScope;

            // Get connectors to map connector names
            const connectors = useConnectorStore.getState().connectors;
            const connectorMap = new Map(connectors.map(c => [c.id, c.name]));

            // If run has embedded connector info, use that
            let connectorName = run.connectorName;

            // If run has connectors object, extract the name from there
            if (!connectorName && run.connectors && connectorId) {
              const connectorInfo = run.connectors[connectorId];
              if (connectorInfo && connectorInfo.name) {
                connectorName = connectorInfo.name;
              }
            }

            // Fallback to connector map from store
            if (!connectorName && connectorId) {
              connectorName = connectorMap.get(connectorId) || '';
            }

            // If still no name, preserve existing one
            if (!connectorName && existingRun.connectorName) {
              connectorName = existingRun.connectorName;
            }

            // Final fallback
            if (!connectorName) {
              connectorName = connectorId
                ? `Connector ${connectorId.substring(0, 8)}...`
                : 'Unknown Connector';
            }

            run.connectorName = connectorName;
          }

          // Merge stage counts, preserving completed stages that may have been removed
          if (existingRun.stageCounts && run.stageCounts) {
            // Create a new stageCounts object that preserves completed stages
            const mergedStageCounts: typeof run.stageCounts = {};

            // First, copy all existing stage data (preserves history)
            Object.entries(existingRun.stageCounts).forEach(([stage, counts]) => {
              // Check if this stage has completed (all docs processed)
              const total = Object.values(counts).reduce((sum: number, count) => sum + (typeof count === 'number' ? count : 0), 0);
              const completed = counts.complete || 0;
              const failed = counts.failed || 0;

              // Preserve stage if it had any data (even if now empty)
              if ((typeof total === 'number' && total > 0) || completed > 0 || failed > 0) {
                mergedStageCounts[stage] = { ...counts };
              }
            });

            // Then, update with new data (overwrites active stages)
            Object.entries(run.stageCounts).forEach(([stage, counts]) => {
              mergedStageCounts[stage] = counts;
            });

            run.stageCounts = mergedStageCounts;
          }

          // Similarly preserve chunk progress data
          if (existingRun.chunkProgress && run.chunkProgress) {
            // Merge chunk progress, preserving completed stage data
            const mergedChunksByStage = {
              ...existingRun.chunkProgress.chunksByStage,
              ...run.chunkProgress.chunksByStage
            };

            // Keep historical percentages for completed stages
            const mergedChunkPercentages = {
              ...existingRun.chunkProgress.chunkPercentages,
              ...run.chunkProgress.chunkPercentages
            };

            run.chunkProgress = {
              ...run.chunkProgress,
              chunksByStage: mergedChunksByStage,
              chunkPercentages: mergedChunkPercentages
            };
          }

          state.runs[idx] = run;
        } else {
          // For new runs, also ensure connector name is populated
          if (!run.connectorName) {
            const connectorId = run.connectorId || run.connectorScope;
            const connectors = useConnectorStore.getState().connectors;
            const connectorMap = new Map(connectors.map(c => [c.id, c.name]));

            let connectorName = run.connectorName;

            if (!connectorName && run.connectors && connectorId) {
              const connectorInfo = run.connectors[connectorId];
              if (connectorInfo && connectorInfo.name) {
                connectorName = connectorInfo.name;
              }
            }

            if (!connectorName && connectorId) {
              connectorName = connectorMap.get(connectorId) || '';
            }

            if (!connectorName) {
              connectorName = connectorId
                ? `Connector ${connectorId.substring(0, 8)}...`
                : 'Unknown Connector';
            }

            run.connectorName = connectorName;
          }

          state.runs.unshift(run);
        }
      });
    },

    clearActiveRuns: () => {
      set(state => {
        state.runs = state.runs.filter(run => run.status !== 'running');
      });
    },

    updateRunProgress: (runId, progress) => {
      set(state => {
        const run = state.runs.find(r => r.id === runId);
        if (run) {
          run.progress = { ...run.progress, ...progress };
        }
      });
    },

    selectRun: runId => {
      set(state => {
        state.selectedRun = runId
          ? state.runs.find(r => r.id === runId) || null
          : null;
      });
    },

    setDateFilter: filter => {
      set(state => {
        state.dateFilter = filter;
        state.pagination.page = 1; // Reset to first page
      });
    },

    setPagination: pagination => {
      set(state => {
        state.pagination = pagination;
      });
    },

    goToPage: page => {
      set(state => {
        state.pagination.page = page;
      });
    },

    setFilters: filters => {
      set(state => {
        state.filters = filters;
        state.pagination.page = 1; // Reset to first page when filters change
      });
    },

    clearRuns: statuses => {
      set(state => {
        // Find runs to clear
        const runsToClear = state.runs.filter(run =>
          statuses.includes(run.status)
        );

        // Add their IDs to the cleared set
        runsToClear.forEach(run => state.clearedRunIds.add(run.id));

        // Save to localStorage
        saveClearedRunIds(state.clearedRunIds);

        // Remove from current state
        state.runs = state.runs.filter(run => !statuses.includes(run.status));

        // Clear selected run if it was removed
        if (state.selectedRun && statuses.includes(state.selectedRun.status)) {
          state.selectedRun = null;
        }
        // Force UI update
        state.stateVersion = (state.stateVersion || 0) + 1;
      });
    },

    clearRun: runId => {
      set(state => {
        state.runs = state.runs.filter(run => run.id !== runId);
        // Track cleared run ID to prevent re-adding
        state.clearedRunIds.add(runId);

        // Save to localStorage
        saveClearedRunIds(state.clearedRunIds);

        // Clear selected run if it was removed
        if (state.selectedRun && state.selectedRun.id === runId) {
          state.selectedRun = null;
        }
        // Force UI update
        state.stateVersion = (state.stateVersion || 0) + 1;
      });
    },

    resetClearedRuns: () => {
      set(state => {
        state.clearedRunIds.clear();
        // Clear from localStorage too
        saveClearedRunIds(state.clearedRunIds);
      });
    },
  }))
);
